/**
 * Validation Statistics Hook
 * 
 * Provides real-time validation counts grouped by priority level.
 * Replaces hardcoded validation counts in validation-filter-panel.tsx
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, getCurrentUserOrganization } from '@/integrations/supabase/client';

export interface ValidationStats {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

/**
 * Fetches validation statistics from the database
 * Groups transactions by AI confidence level (acts as validation priority)
 * 
 * Priority mapping based on AI confidence:
 * - Critical: < 50% confidence
 * - High: 50-70% confidence  
 * - Medium: 70-85% confidence
 * - Low: >= 85% confidence
 */
const fetchValidationStats = async (): Promise<ValidationStats> => {
  try {
    // Get current user's organization using same method as transactions.ts
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg || !userOrg.company_id) {
      return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    }

    // Fetch transactions that need validation (human_verified = false/null)
    const { data: transactions, error } = await supabase
      .from('transactions')
      .select('ai_classification_confidence, id')
      .or('human_verified.is.null,human_verified.eq.false')
      .eq('organization_id', userOrg.company_id);

    if (error) {
      console.error('Error fetching validation stats:', error);
      return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    }

    if (!transactions || transactions.length === 0) {
      return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    }

    // Count by priority based on AI confidence
    const stats = transactions.reduce((acc, transaction) => {
      const confidence = transaction.ai_classification_confidence || 0;
      
      // Map confidence to priority
      if (confidence < 50) acc.critical++;
      else if (confidence < 70) acc.high++;
      else if (confidence < 85) acc.medium++;
      else acc.low++;
      
      acc.total++;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0, total: 0 } as ValidationStats);

    return stats;
  } catch (error) {
    console.error('Error in fetchValidationStats:', error);
    return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
  }
};

/**
 * Hook to get validation statistics
 * 
 * @param options - React Query options
 * @returns Validation statistics with loading and error states
 */
export const useValidationStats = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: ['validation-stats'],
    queryFn: fetchValidationStats,
    refetchInterval: options?.refetchInterval || 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 2,
  });
};

/**
 * Alternative: Get validation stats by mandate
 */
export const useValidationStatsByMandate = (mandateId?: string) => {
  return useQuery({
    queryKey: ['validation-stats', mandateId],
    queryFn: async (): Promise<ValidationStats> => {
      if (!mandateId) {
        return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
      }

      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('ai_classification_confidence, id')
        .or('human_verified.is.null,human_verified.eq.false')
        .eq('mandate_id', mandateId);

      if (error || !transactions) {
        return { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
      }

      return transactions.reduce((acc, transaction) => {
        const confidence = transaction.ai_classification_confidence || 0;
        if (confidence < 50) acc.critical++;
        else if (confidence < 70) acc.high++;
        else if (confidence < 85) acc.medium++;
        else acc.low++;
        acc.total++;
        return acc;
      }, { critical: 0, high: 0, medium: 0, low: 0, total: 0 } as ValidationStats);
    },
    enabled: !!mandateId,
    refetchInterval: 30000,
    staleTime: 10000,
  });
};
